import React, { useRef, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@material-ui/core'
import { BasePage as CngBasePage, components } from 'cng-web-lib'
import { useForm, FormProvider } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MpciApiClient from '../Common/services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'
import SnackBar from 'src/components/era-package/era/SnackBar'
import CngSection from 'src/components/cngcomponents/CngSection'
import MpciTable from '../Common/components/MpciTable'

const {
  button: { CngButton },
  form: {
    field: { CngTextField }
  },
  CngGridItem
} = components

const DEFAULT_VALUES = {
  codeType: '',
  code: '',
  description: ''
}

const COLUMNS = [
  { field: 'codeType', title: 'Code Type', sortKey: 'codeType' },
  { field: 'code', title: 'Code', sortKey: 'code' },
  { field: 'description', title: 'Description', sortKey: 'description' }
]

const FILTERS = [
  { name: 'codeType', label: 'Code Type', type: 'text' },
  { name: 'code', label: 'Code', type: 'text' },
  { name: 'description', label: 'Description', type: 'text' }
]

function CodeMaintenanceViewDialog({ open, record, onClose, onEdit }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>Code Maintenance Details</Typography>
          <IconButton size='small' onClick={onClose}>
            <FontAwesomeIcon icon={['fal', 'times']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant='caption' color='textSecondary'>Code Type</Typography>
            <Typography variant='body1'>{record?.codeType || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant='caption' color='textSecondary'>Code</Typography>
            <Typography variant='body1'>{record?.code || '—'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption' color='textSecondary'>Description</Typography>
            <Typography variant='body1'>{record?.description || '—'}</Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <CngButton variant='outlined' onClick={onClose}>
          Close
        </CngButton>
        <CngButton
          color='primary'
          startIcon={<FontAwesomeIcon icon={['fal', 'pen']} />}
          onClick={() => { onClose(); onEdit(record) }}
        >
          Edit
        </CngButton>
      </DialogActions>
    </Dialog>
  )
}

function DeleteConfirmDialog({ open, record, onConfirm, onCancel, isDeleting }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle>Delete Code</DialogTitle>
      <DialogContent>
        <Typography variant='body2'>
          Are you sure you want to delete{' '}
          <strong>
            [{record?.codeType}] {record?.code}
          </strong>
          ? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <CngButton variant='outlined' onClick={onCancel} disabled={isDeleting}>
          Cancel
        </CngButton>
        <CngButton
          color='secondary'
          onClick={onConfirm}
          disabled={isDeleting}
          shouldShowProgress={isDeleting}
        >
          Delete
        </CngButton>
      </DialogActions>
    </Dialog>
  )
}

function CloneConfirmDialog({ open, record, onConfirm, onCancel, isCloning }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle>Clone Code</DialogTitle>
      <DialogContent>
        <Typography variant='body2'>
          Clone{' '}
          <strong>
            [{record?.codeType}] {record?.code}
          </strong>{' '}
          into a new entry?
        </Typography>
      </DialogContent>
      <DialogActions>
        <CngButton variant='outlined' onClick={onCancel} disabled={isCloning}>
          Cancel
        </CngButton>
        <CngButton
          color='primary'
          onClick={onConfirm}
          disabled={isCloning}
          shouldShowProgress={isCloning}
          startIcon={<FontAwesomeIcon icon={['fal', 'copy']} />}
        >
          Clone
        </CngButton>
      </DialogActions>
    </Dialog>
  )
}

function CodeMaintenanceFormDialog({ open, mode, record, onClose, onSaved }) {
  const isEdit = mode === 'edit'
  const methods = useForm({ defaultValues: DEFAULT_VALUES })
  const [isSaving, setIsSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  React.useEffect(() => {
    if (open) {
      if (isEdit && record) {
        methods.reset({
          codeType: record.codeType ?? '',
          code: record.code ?? '',
          description: record.description ?? ''
        })
      } else {
        methods.reset(DEFAULT_VALUES)
      }
      setErrorMsg('')
    }
  }, [open, isEdit, record]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = methods.handleSubmit((formData) => {
    setIsSaving(true)
    setErrorMsg('')

    const payload = {
      codeType: formData.codeType,
      code: formData.code,
      description: formData.description || undefined
    }

    if (isEdit) {
      MpciApiClient.put(
        `${UaeMpciApiUrls.CODE_MAINTENANCE_UPDATE}/${record.id}`,
        payload,
        () => {
          setIsSaving(false)
          onSaved('Updated successfully.')
        },
        (err) => {
          setIsSaving(false)
          const status = err?.response?.status
          setErrorMsg(
            status === 409
              ? 'A code with the same Code Type and Code already exists.'
              : 'Failed to update. Please try again.'
          )
        }
      )
    } else {
      MpciApiClient.post(
        UaeMpciApiUrls.CODE_MAINTENANCE_CREATE,
        payload,
        () => {
          setIsSaving(false)
          onSaved('Created successfully.')
        },
        (err) => {
          setIsSaving(false)
          const status = err?.response?.status
          setErrorMsg(
            status === 409
              ? 'A code with the same Code Type and Code already exists.'
              : 'Failed to create. Please try again.'
          )
        }
      )
    }
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>
            {isEdit ? 'Edit Code' : 'New Code'}
          </Typography>
          <IconButton size='small' onClick={onClose}>
            <FontAwesomeIcon icon={['fal', 'times']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {errorMsg && (
              <Box mb={2}>
                <Typography variant='body2' color='error'>
                  {errorMsg}
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <CngGridItem xs={12} sm={6}>
                <CngTextField
                  name='codeType'
                  label='Code Type *'
                  size='small'
                  inputProps={{ maxLength: 50 }}
                />
              </CngGridItem>

              <CngGridItem xs={12} sm={6}>
                <CngTextField
                  name='code'
                  label='Code *'
                  size='small'
                  inputProps={{ maxLength: 50 }}
                />
              </CngGridItem>

              <CngGridItem xs={12}>
                <CngTextField
                  name='description'
                  label='Description'
                  size='small'
                  inputProps={{ maxLength: 256 }}
                />
              </CngGridItem>
            </Grid>
          </DialogContent>

          <DialogActions>
            <CngButton variant='outlined' onClick={onClose} disabled={isSaving}>
              Cancel
            </CngButton>
            <CngButton
              type='submit'
              color='primary'
              disabled={isSaving}
              shouldShowProgress={isSaving}
              startIcon={<FontAwesomeIcon icon={['fal', 'save']} />}
            >
              {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </CngButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

function CodeMaintenanceContent() {
  const tableRef = useRef(null)

  const [viewDialog, setViewDialog] = useState({ open: false, record: null })
  const [formDialog, setFormDialog] = useState({ open: false, mode: 'add', record: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null })
  const [cloneDialog, setCloneDialog] = useState({ open: false, record: null })
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCloning, setIsCloning] = useState(false)

  const [successSnack, setSuccessSnack] = useState({ open: false, message: '' })
  const [errorSnack, setErrorSnack] = useState({ open: false, message: '' })

  function openView(row) {
    setViewDialog({ open: true, record: row })
  }

  function openAdd() {
    setFormDialog({ open: true, mode: 'add', record: null })
  }

  function openEdit(row) {
    setFormDialog({ open: true, mode: 'edit', record: row })
  }

  function openDelete(row) {
    setDeleteDialog({ open: true, record: row })
  }

  function openClone(row) {
    setCloneDialog({ open: true, record: row })
  }

  function handleFormSaved(message) {
    setFormDialog({ open: false, mode: 'add', record: null })
    setSuccessSnack({ open: true, message })
    if (tableRef.current) tableRef.current.performRefresh()
  }

  function handleDelete() {
    const { record } = deleteDialog
    if (!record) return

    setIsDeleting(true)
    MpciApiClient.del(
      `${UaeMpciApiUrls.CODE_MAINTENANCE_DELETE}/${record.id}`,
      {},
      () => {
        setIsDeleting(false)
        setDeleteDialog({ open: false, record: null })
        setSuccessSnack({ open: true, message: 'Deleted successfully.' })
        if (tableRef.current) tableRef.current.performRefresh()
      },
      (err) => {
        setIsDeleting(false)
        console.error('[CodeMaintenance] delete failed', err)
        setDeleteDialog({ open: false, record: null })
        setErrorSnack({ open: true, message: 'Failed to delete. Please try again.' })
      }
    )
  }

  function handleClone() {
    const { record } = cloneDialog
    if (!record) return

    setIsCloning(true)
    MpciApiClient.post(
      `${UaeMpciApiUrls.CODE_MAINTENANCE_CLONE}/${record.id}`,
      {},
      () => {
        setIsCloning(false)
        setCloneDialog({ open: false, record: null })
        setSuccessSnack({ open: true, message: 'Cloned successfully.' })
        if (tableRef.current) tableRef.current.performRefresh()
      },
      (err) => {
        setIsCloning(false)
        console.error('[CodeMaintenance] clone failed', err)
        setCloneDialog({ open: false, record: null })
        setErrorSnack({ open: true, message: 'Failed to clone. Please try again.' })
      }
    )
  }

  const tableActions = [
    {
      component: () => (
        <CngButton
          color='primary'
          size='small'
          startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
          onClick={openAdd}
        >
          New Code
        </CngButton>
      )
    }
  ]

  const rowActions = [
    {
      label: 'Edit',
      icon: <FontAwesomeIcon icon={['fal', 'pen']} />,
      onClick: (row) => openEdit(row)
    },
    {
      label: 'Clone',
      icon: <FontAwesomeIcon icon={['fal', 'copy']} />,
      onClick: (row) => openClone(row)
    },
    {
      label: 'Delete',
      icon: <FontAwesomeIcon icon={['fal', 'trash']} />,
      onClick: (row) => openDelete(row)
    }
  ]

  return (
    <>
      <SnackBar
        title='Success'
        type='success'
        open={successSnack.open}
        onClose={() => setSuccessSnack({ open: false, message: '' })}
        duration={3000}
      >
        {successSnack.message}
      </SnackBar>

      <SnackBar
        title='Error'
        type='error'
        open={errorSnack.open}
        onClose={() => setErrorSnack({ open: false, message: '' })}
      >
        {errorSnack.message}
      </SnackBar>

      <Card>
        <CardContent>
          <CngSection
            title='Code Maintenance'
            icon={<FontAwesomeIcon icon={['fal', 'list-alt']} />}
          >
            <Box mb={1}>
              <Typography variant='body2' color='textSecondary'>
                Manage reference code entries. Each entry is identified by a code type and a code value.
              </Typography>
            </Box>

            <MpciTable
              ref={tableRef}
              url={UaeMpciApiUrls.CODE_MAINTENANCE_LIST}
              columns={COLUMNS}
              filters={FILTERS}
              actions={tableActions}
              rowActions={rowActions}
              onRowClick={(row) => openView(row)}
              defaultSort={{ field: 'codeType', direction: 'ASC' }}
              flat
            />
          </CngSection>
        </CardContent>
      </Card>

      <CodeMaintenanceViewDialog
        open={viewDialog.open}
        record={viewDialog.record}
        onClose={() => setViewDialog({ open: false, record: null })}
        onEdit={(row) => openEdit(row)}
      />

      <CodeMaintenanceFormDialog
        open={formDialog.open}
        mode={formDialog.mode}
        record={formDialog.record}
        onClose={() => setFormDialog({ open: false, mode: 'add', record: null })}
        onSaved={handleFormSaved}
      />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        record={deleteDialog.record}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, record: null })}
        isDeleting={isDeleting}
      />

      <CloneConfirmDialog
        open={cloneDialog.open}
        record={cloneDialog.record}
        onConfirm={handleClone}
        onCancel={() => setCloneDialog({ open: false, record: null })}
        isCloning={isCloning}
      />
    </>
  )
}

function CodeMaintenancePage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  return (
    <CngBasePage
      renderPage={() => <CodeMaintenanceContent {...props} />}
      extraCngBreadcrumbNameMap={breadcrumbNameMap}
    />
  )
}

export default CodeMaintenancePage
